// The guided setup wizard's STEP MANAGER: given the quote's sections and the owner's current setup state, it
// produces the exact ordered steps the wizard walks -- and ONLY the ones still needed. A material price, a
// machine, or a machine's price the owner already has is not a step; as each is added the manager is re-run with
// fresh state and that step drops out, so "Step X of N" always tells the truth about what is left.
//
// The steps, in order:
//   Stage 1  QUANTITY in order -- always, once, for the whole quote.
//   then, PER priceable nav SECTION that actually chooses (e.g. Carton), only the setup steps it is MISSING,
//   then its two choice steps:
//     material price          -- add a unit price for the section's material            (own lines, if unpriced)
//     printing machine        -- add the printing machine itself                        (own lines, if it prints
//                                                                                          and has none)
//     printing machine price  -- add that machine's unit price                          (own lines, if unpriced)
//     die-cutting machine     -- add the die-cutting machine itself                     (own lines, if none)
//     die-cutting machine price -- add that machine's unit price                        (own lines, if unpriced)
//     manufacturing line      -- pick one manufacturing line
//     solution                -- pick one solution  (the LAST step; leaving it, Done, closes the wizard)
//
// PRICING IS NOT A STEP: once the last solution is chosen the wizard closes onto the page's Pricing tab, which is
// where the priced result is read. It is the OUTCOME of finishing, not a stage to walk, so it is not counted --
// a fully-unconfigured single Carton is 8 steps (quantity + its five setup steps + line + solution).
//
// A MACHINE is TWO steps -- adding the machine and pricing it -- because either can be already done on its own:
// a manufacturing line is FOUND from a machine that has no price, and it cannot be quoted until the price exists,
// so an owner who has the machine but not its price is led straight to the price step (the machine step is
// skipped). This is the reported case (a found, scored line the owner could never actually be quoted).
//
// TWO kinds of section are skipped entirely, both because they choose nothing:
//   * the nav builder's 'general' catch-all, which is not a manufacturing part section;
//   * a LINKED section, which takes its manufacturing line and solution from another section (see
//     linkedNavSection.js) -- walking someone through stages that decide nothing would imply the answer was
//     theirs to give.
//
// STANDARD manufacturing lines are never gated: they use the example account, which has every material and
// machine priced, so a Standard quote has no setup steps -- just quantity, the choice steps, and pricing.
//
// requirementsBySection and the has*() readers are passed IN rather than read from the window here, so this stays
// a pure function the caller can test at any state -- FrankSetupWizard reads the live sections, the server-seeded
// requirements and the live counts, and hands them over.

const OWN = 'own';

// The setup steps ONE section is still missing, in the order they must be done: its material price, then for each
// machine row (printing before die-cutting) the machine itself and then its price -- each included only when the
// owner does not already have it. Empty on Standard, or once the section is fully set up.
const sectionSetupSteps = (requirement, sectionIndex, label, {
  source, hasMaterialPrice, hasMachine, hasMachinePrice
}) => {
  if (source !== OWN || !requirement) { return []; }

  const steps = [];
  // Server-seeded (Frank::SectionSetupRequirements), so snake_case: material_key, board_thickness_mm, and
  // machines[{ key, allowed_types }].
  const materialKey = requirement.material_key;
  const machines = requirement.machines || [];

  if (materialKey && !hasMaterialPrice(materialKey)) {
    // board_thickness_mm narrows the material step's frequently-used list to the drawing's thickness (hard
    // cardboard only; null otherwise leaves it unfiltered).
    steps.push({ type: 'material', materialKey, boardThicknessMm: requirement.board_thickness_mm, sectionIndex, label });
  }

  // allowedTypes (the row's allowed_types) is passed to the readers, so a machine of the WRONG type does not
  // satisfy the step: a carton section needs a 'cartons' die-cutter, and an owner who has only a
  // 'hard_cardboards' one is still walked through adding (and pricing) the one this section can use.
  machines.forEach(({ key: machineKey, allowed_types: allowedTypes }) => {
    if (!hasMachine(machineKey, allowedTypes)) {
      steps.push({ type: 'machine_entry', machineKey, allowedTypes, sectionIndex, label });
    }
    if (!hasMachinePrice(machineKey, allowedTypes)) {
      steps.push({ type: 'machine_price', machineKey, sectionIndex, label });
    }
  });

  return steps;
};

// The full step list -- quantity, then each choosing section's missing setup steps followed by its line and
// solution. Every entry is a stage the wizard walks; the count (.length) is the "of N" the user sees.
export const buildSetupWizardStages = (navSections, {
  linkedSectionIndexes = [],
  source = 'standard',
  requirementsBySection = {},
  hasMaterialPrice = () => true,
  hasMachine = () => true,
  hasMachinePrice = () => true,
} = {}) => {
  const choosingSections = (navSections || [])
    .map((section, index) => ({ section, index }))
    .filter(({ section }) => section.part_type !== 'general')
    .filter(({ index }) => !linkedSectionIndexes.includes(index));

  const sectionStages = choosingSections.flatMap(({ section, index }) => {
    const label = section.label || section.part_type;
    return [
      ...sectionSetupSteps(requirementsBySection[index], index, label, {
        source, hasMaterialPrice, hasMachine, hasMachinePrice
      }),
      { type: 'line', sectionIndex: index, label },
      { type: 'solution', sectionIndex: index, label },
    ];
  });

  return [
    { type: 'quantity' },
    ...sectionStages,
  ];
};

export default buildSetupWizardStages;
